import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getCRMContacts } from '../../crm/api';
import { readAnyContactName } from '../utils';
import { useOutsideClick, useMobileScrollLock } from '@/common/hooks';

function getNextPageParam(lastPage) {
  if (!lastPage?.next) return undefined;
  try {
    const url = new URL(lastPage.next, window.location.origin);
    return url.searchParams.get('page') || undefined;
  } catch {
    return undefined;
  }
}

export function ContactPicker({
  companyId,
  value,
  selectedLabel,
  onChange,
  error,
  disabled,
  onCreateContact,
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useMobileScrollLock(open);
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });
  const instanceId = useId();
  const sentinelRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useOutsideClick(
    [containerRef, dropdownRef],
    () => {
      setOpen(false);
    },
    open,
  );

  useEffect(() => {
    function handleOtherDropdownOpened(e) {
      if (e.detail?.id !== instanceId) {
        setOpen(false);
      }
    }
    document.addEventListener('dropdown:opened', handleOtherDropdownOpened);
    return () => {
      document.removeEventListener(
        'dropdown:opened',
        handleOtherDropdownOpened,
      );
    };
  }, [instanceId]);

  const query = useInfiniteQuery({
    queryKey: ['appointment-contacts', companyId, debounced],
    queryFn: ({ pageParam = 1 }) => {
      const params = { page: pageParam, page_size: 20 };
      if (debounced) params.search = debounced;
      return getCRMContacts(companyId, params);
    },
    getNextPageParam,
    enabled: !!companyId,
    initialPageParam: 1,
  });

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    query;

  const contacts =
    data?.pages?.flatMap((p) => p?.results || (Array.isArray(p) ? p : [])) ||
    [];

  useEffect(() => {
    if (!open || !sentinelRef.current) return undefined;
    const node = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '80px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const displayLabel =
    selectedLabel ||
    (value ? contacts.find((c) => String(c.id) === String(value)) : null);

  const labelText =
    typeof displayLabel === 'string'
      ? displayLabel
      : readAnyContactName(displayLabel) || (value ? `Contact #${value}` : '');

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue(labelText || '');
    if (!open) {
      setSearch('');
    }
  }, [open, labelText]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (open && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const placement =
          spaceBelow < 240 && spaceAbove > spaceBelow ? 'top' : 'bottom';
        setDropdownStyle({
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          ...(placement === 'bottom'
            ? { top: rect.bottom + 4 }
            : { bottom: window.innerHeight - rect.top + 4 }),
        });
      }
    };
    const onScroll = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target))
        return;
      updatePosition();
    };
    updatePosition();
    if (open) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', onScroll, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const handleOpen = (next) => {
    if (next) {
      document.dispatchEvent(
        new CustomEvent('dropdown:opened', { detail: { id: instanceId } }),
      );
    }
    setOpen(next);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setSearch(e.target.value);
    if (!open) handleOpen(true);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`relative flex items-stretch w-full h-11 rounded-xl border transition-colors ${
          error
            ? 'border-status-error'
            : open
              ? 'border-[#50AB3D] ring-2 ring-[#50AB3D]/15'
              : 'border-[#EBF4EA] hover:border-[#50AB3D]'
        } ${disabled ? 'opacity-60 bg-white' : 'bg-white'}`}
      >
        <input
          type="text"
          disabled={disabled}
          value={open ? inputValue : labelText || ''}
          onChange={handleInputChange}
          onFocus={(e) => {
            handleOpen(true);
            e.target.select();
          }}
          placeholder="Search or select contact..."
          className="flex-1 bg-transparent px-3 text-sm font-medium text-[#1F2241] focus:outline-none disabled:cursor-not-allowed cursor-text rounded-l-xl"
        />
        {/* <div className="w-px bg-[#EBF4EA] my-2" /> */}
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            handleOpen(!open);
          }}
          className="w-11 flex-shrink-0 flex items-center justify-center text-[#868686] hover:text-[#50AB3D] cursor-pointer disabled:cursor-not-allowed rounded-r-xl transition-colors hover:bg-[#F5FAF4]"
        >
          <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-180 text-[#50AB3D]' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ ...dropdownStyle, overscrollBehavior: 'contain' }}
            data-portal-dropdown="true"
            className="z-[99999] bg-white rounded-xl border border-[#EBF4EA] shadow-xl overflow-hidden"
          >
            <div className="max-h-56 overflow-y-auto custom-scrollbar">
              {isLoading && (
                <div className="px-3 py-4 text-sm text-[#868686]">
                  Loading...
                </div>
              )}
              {!isLoading && contacts.length === 0 && (
                <div className="flex flex-col">
                  <div className="px-3 pt-3 pb-1 text-sm text-[#868686]">
                    No contacts found
                  </div>
                  {onCreateContact && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        // Prevent the input blur/close before the click registers
                        e.preventDefault();
                      }}
                      onClick={() => {
                        setOpen(false);
                        onCreateContact?.(search || debounced);
                      }}
                      className="mx-2 mb-2 mt-1 flex items-center gap-2 px-3 py-2.5 rounded-lg
                                 text-sm font-medium text-[#50AB3D] hover:bg-[#EBF4EA] transition-colors"
                    >
                      <span className="w-5 h-5 rounded-md bg-[#50AB3D] flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M12 5v14M5 12h14"
                          />
                        </svg>
                      </span>
                      Create New Contact
                    </button>
                  )}
                </div>
              )}
              {contacts.map((contact) => {
                const name =
                  readAnyContactName(contact) || `Contact #${contact.id}`;
                const selected = String(contact.id) === String(value);
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => {
                      onChange?.({
                        id: contact.id,
                        name,
                        contact,
                      });
                      setOpen(false);
                    }}
                    className={`w-full px-3 py-2.5 text-left text-sm hover:bg-[#EBF4EA] ${
                      selected
                        ? 'bg-[#EBF4EA] text-[#50AB3D] font-semibold'
                        : 'text-[#1F2241]'
                    }`}
                  >
                    <div className="truncate">{name}</div>
                    {(contact.phone || contact.email) && (
                      <div className="text-xs text-[#868686] truncate">
                        {[contact.phone, contact.email]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </button>
                );
              })}
              <div ref={sentinelRef} className="h-4" />
              {isFetchingNextPage && (
                <div className="px-3 py-2 text-xs text-[#868686]">
                  Loading more...
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {error && <p className="mt-1 text-xs text-status-error">{error}</p>}
    </div>
  );
}
